/**
 * Publishing targets.
 *
 * Source of truth: docs/decisions/2026-04-23-v1-core-decisions.md Decision 5,
 * docs/02-domain-map.md Section 13, docs/03-workflows.md Section 11.
 *
 * Rules:
 * - V1 publishes only to the seven targets below.
 * - V2 targets are documented but NOT implemented in V1.
 * - Each new target requires a platform adapter and a decisions log entry.
 */

/**
 * The seven V1 publishing targets. Locked.
 */
export const PUBLISH_TARGETS_V1 = [
  'instagram_feed',
  'instagram_story',
  'instagram_reel',
  'facebook_post',
  'facebook_story',
  'tiktok_video',
  'snapchat_story',
] as const;
export type PublishTargetV1 = (typeof PUBLISH_TARGETS_V1)[number];

/**
 * Documented V2 targets. NOT available in V1.
 */
export const PUBLISH_TARGETS_V2 = [
  'x_post',
  'linkedin_post',
  'youtube_video',
  'snapchat_spotlight',
] as const;
export type PublishTargetV2 = (typeof PUBLISH_TARGETS_V2)[number];

/**
 * Union of current V1 targets. Use this in publishing code to force an
 * exhaustive switch that breaks the build when a new target is added.
 */
export type PublishTarget = PublishTargetV1;

/**
 * Parent platform each target publishes into. Useful for OAuth connection
 * scoping and per-platform adapter dispatch.
 */
export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'snapchat'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/**
 * Lifecycle for a stored social connection. `active` is the only state in
 * which the publishing pipeline accepts the connection for scheduling.
 * `reauthorization_required` / `disabled` / `revoked` are inert states that
 * block new publish_jobs at the database boundary.
 *
 * Source: docs/07-schema-boundaries.md §3.14, docs/08-rbac-rls-matrix.md §9.8,
 * SECURITY.md §6.
 */
export const SOCIAL_CONNECTION_STATUSES = [
  'active',
  'reauthorization_required',
  'disabled',
  'revoked',
] as const;
export type SocialConnectionStatus = (typeof SOCIAL_CONNECTION_STATUSES)[number];

/**
 * Lifecycle states for `publish_jobs`. Terminal states are
 * `succeeded`, `failed`, and `cancelled`; the DB enforces timestamp
 * coherence (completed_at / failed_at / cancelled_at) on the table.
 *
 * Source: docs/03-workflows.md §11, docs/07-schema-boundaries.md §3.14,
 * docs/08-rbac-rls-matrix.md §9.8.
 */
export const PUBLISH_JOB_STATUSES = [
  'scheduled',
  'processing',
  'succeeded',
  'failed',
  'cancelled',
] as const;
export type PublishJobStatus = (typeof PUBLISH_JOB_STATUSES)[number];

/**
 * Outcome of a single execution attempt against a platform adapter.
 * The attempts table is append-only.
 */
export const PUBLISH_ATTEMPT_STATUSES = ['succeeded', 'failed'] as const;
export type PublishAttemptStatus = (typeof PUBLISH_ATTEMPT_STATUSES)[number];

/**
 * Log severity for `publish_log`. The log table is append-only and
 * service-written.
 */
export const PUBLISH_LOG_LEVELS = ['info', 'warning', 'error'] as const;
export type PublishLogLevel = (typeof PUBLISH_LOG_LEVELS)[number];

/**
 * Lifecycle states for `idempotency_keys`. `in_progress` is the initial
 * lock state; `completed` / `failed` are terminal; `expired` flips via TTL
 * sweep.
 *
 * Source: docs/07-schema-boundaries.md §3.18, SECURITY.md §8.
 */
export const IDEMPOTENCY_KEY_STATUSES = ['in_progress', 'completed', 'failed', 'expired'] as const;
export type IdempotencyKeyStatus = (typeof IDEMPOTENCY_KEY_STATUSES)[number];

/**
 * Task 280L — Content-format ↔ platform ↔ publish-target mapping (Phase 0 of the
 * AI calendar platform/placement redesign).
 *
 * PURELY ADDITIVE. `PUBLISH_TARGETS_V1` stays the locked, placement-level publish
 * currency; this layer lets later phases drive *selection* at the parent-platform
 * level (Instagram / TikTok / Snapchat) and a chosen *asset format*, then resolve
 * back to the existing `PublishTargetV1` placements the save/publish pipeline
 * already understands. Nothing here is wired into a caller yet.
 *
 * The conceptual split:
 *   - asset FORMAT     — what kind of creative the content unit is.
 *   - parent PLATFORM  — `SocialPlatform` (instagram/facebook/tiktok/snapchat).
 *   - publish TARGET   — `PublishTargetV1` (the platform×placement enum).
 */

/**
 * The four V1 asset formats a content unit can take. Locked product decision
 * (Task 280L). Names align with the worker's free-text `asset_format` hints.
 */
export const ASSET_FORMATS = ['static_image', 'carousel', 'vertical_video', 'story'] as const;
export type AssetFormat = (typeof ASSET_FORMATS)[number];

/**
 * Asset format → the publish targets that asset can be distributed to in AI V1.
 * Product decisions (Task 280L, amended by Task 282):
 *   - Snapchat is vertical/story/video → no static image; reached by `vertical_video` + `story`.
 *   - Facebook is OUT of AI V1        → no `facebook_*` target appears here, even though the
 *     publish enum still supports it.
 *   - Task 282: a CAROUSEL may also publish to TikTok (as a vertical/story-sized carousel) via
 *     the existing `tiktok_video` target — so `tiktok_video` is now reachable via `vertical_video`
 *     OR `carousel`. `publish_target_v1` is unchanged (no new enum value); only this format→target
 *     matrix is widened. Snapchat carousel is intentionally NOT added (Snapchat stays story-only).
 * Order is meaningful (it drives downstream variant order) and is preserved by
 * `resolveCompatibleTargets`.
 */
export const ASSET_FORMAT_TARGETS: Readonly<Record<AssetFormat, readonly PublishTargetV1[]>> = {
  static_image: ['instagram_feed'],
  carousel: ['instagram_feed', 'tiktok_video'],
  vertical_video: ['instagram_reel', 'tiktok_video', 'snapchat_story'],
  story: ['instagram_story', 'snapchat_story'],
} as const;

/**
 * Parent platform → its `PublishTargetV1` placements. COMPLETE over all four
 * platforms and all seven targets (Facebook included) so the reverse lookup is
 * total. (AI-V1 *selection* still excludes Facebook — that policy lives in the
 * asset-format matrix above and in the FE, not in this structural map.)
 */
export const PLATFORM_TARGETS: Readonly<Record<SocialPlatform, readonly PublishTargetV1[]>> = {
  instagram: ['instagram_feed', 'instagram_story', 'instagram_reel'],
  facebook: ['facebook_post', 'facebook_story'],
  tiktok: ['tiktok_video'],
  snapchat: ['snapchat_story'],
} as const;

/**
 * Reverse of `PLATFORM_TARGETS`: every `PublishTargetV1` → its parent platform.
 * Total over all seven targets.
 */
export const TARGET_TO_PLATFORM: Readonly<Record<PublishTargetV1, SocialPlatform>> = {
  instagram_feed: 'instagram',
  instagram_story: 'instagram',
  instagram_reel: 'instagram',
  facebook_post: 'facebook',
  facebook_story: 'facebook',
  tiktok_video: 'tiktok',
  snapchat_story: 'snapchat',
} as const;

/** The parent platform a publish target belongs to. */
export function targetToPlatform(target: PublishTargetV1): SocialPlatform {
  return TARGET_TO_PLATFORM[target];
}

/**
 * Resolve the publish targets to generate / fan out for: the targets compatible
 * with `format` that also belong to one of the `platforms` selected by the user.
 * Pure intersection — preserves the `ASSET_FORMAT_TARGETS` order and returns `[]`
 * when there is no overlap (e.g. a static image with only TikTok selected).
 */
export function resolveCompatibleTargets(
  format: AssetFormat,
  platforms: readonly SocialPlatform[],
): PublishTargetV1[] {
  const selected = new Set<SocialPlatform>(platforms);
  return ASSET_FORMAT_TARGETS[format].filter((target) => selected.has(targetToPlatform(target)));
}

/**
 * Task 282 — one requested output group: a single asset format and the publish
 * targets it resolves to among the selected parent platforms. Represents "one
 * shared creative per format, fanned out to these targets" — the unit the
 * platform-first / multi-select-format picker (Phase 2) and the "one shared asset
 * per format" generation (Phase 3) operate on.
 */
export interface RequestedOutput {
  readonly format: AssetFormat;
  readonly targets: readonly PublishTargetV1[];
}

/**
 * Platform-first, multi-format resolution: given the user's selected parent
 * platforms and selected asset formats, return one `RequestedOutput` per format
 * (deduped, in the input order) whose targets are the format's compatible targets
 * among the selected platforms. Formats with no compatible target are omitted, so
 * the result is exactly the set of (format → targets) groups to generate. Pure.
 */
export function resolveRequestedOutputs(
  formats: readonly AssetFormat[],
  platforms: readonly SocialPlatform[],
): RequestedOutput[] {
  const out: RequestedOutput[] = [];
  const seen = new Set<AssetFormat>();
  for (const format of formats) {
    if (seen.has(format)) continue;
    seen.add(format);
    const targets = resolveCompatibleTargets(format, platforms);
    if (targets.length > 0) out.push({ format, targets });
  }
  return out;
}

/**
 * Task 284 — the wire shape of one requested-output group as sent in the
 * `generate-content-calendar-draft` request `requested_outputs[]`. The wire uses
 * snake_case `asset_format` (the internal {@link RequestedOutput} uses `format`).
 * This is the OPTIONAL, additive Phase-3 request contract that lets the worker
 * honor per-format intent ("one shared asset per format"); legacy callers send
 * only the flat `platforms` union and never this field.
 */
export interface RequestedOutputWire {
  readonly asset_format: AssetFormat;
  readonly targets: readonly PublishTargetV1[];
}

/**
 * Stable, machine-readable reasons {@link validateRequestedOutputs} can reject a
 * wire `requested_outputs` array. The caller (e.g. the edge function) maps these
 * to its own error envelope + a field path built from `index` / `targetIndex`.
 */
export type RequestedOutputsError =
  | { readonly code: 'must_be_array' }
  | { readonly code: 'must_be_non_empty' }
  | { readonly code: 'group_must_be_object'; readonly index: number }
  | { readonly code: 'invalid_asset_format'; readonly index: number }
  | { readonly code: 'duplicate_format'; readonly index: number }
  | { readonly code: 'targets_must_be_non_empty_array'; readonly index: number }
  | { readonly code: 'invalid_target'; readonly index: number; readonly targetIndex: number }
  | { readonly code: 'incompatible_target'; readonly index: number; readonly targetIndex: number }
  | {
      readonly code: 'target_not_in_platforms';
      readonly index: number;
      readonly targetIndex: number;
    }
  | { readonly code: 'duplicate_target'; readonly index: number; readonly targetIndex: number };

/** Result of {@link validateRequestedOutputs}: normalized groups, or one error. */
export type RequestedOutputsResult =
  | { readonly ok: true; readonly value: RequestedOutput[] }
  | { readonly ok: false; readonly error: RequestedOutputsError };

/**
 * Task 284 — strictly validate + normalize a wire `requested_outputs` array
 * against the Task 280L/282 matrix and the request's resolved `platforms` union.
 * Pure: never throws; returns a discriminated result the caller maps to its own
 * error type. The legacy flat `platforms` field stays the publish/storage
 * currency, so every requested target must also appear in `platformsUnion`.
 *
 * Rules (all enforced, first failure wins, deterministic order):
 *   - the value is a non-empty array;
 *   - each group is an object with a valid `asset_format` and a non-empty
 *     `targets` array; no asset format repeats across groups;
 *   - each target is a real `PublishTargetV1`, is compatible with its format
 *     (∈ `ASSET_FORMAT_TARGETS[format]` → Facebook is structurally impossible),
 *     is present in `platformsUnion`, and is unique within its group.
 *
 * Returns normalized `RequestedOutput[]` (internal `format` field) in input order,
 * targets kept in their given order.
 */
export function validateRequestedOutputs(
  raw: unknown,
  platformsUnion: readonly PublishTargetV1[],
): RequestedOutputsResult {
  if (!Array.isArray(raw)) return { ok: false, error: { code: 'must_be_array' } };
  if (raw.length === 0) return { ok: false, error: { code: 'must_be_non_empty' } };
  const allowed = new Set<string>(platformsUnion);
  const validFormat = new Set<string>(ASSET_FORMATS);
  const validTarget = new Set<string>(PUBLISH_TARGETS_V1);
  const seenFormats = new Set<AssetFormat>();
  const out: RequestedOutput[] = [];
  for (let index = 0; index < raw.length; index += 1) {
    const group = raw[index];
    if (typeof group !== 'object' || group === null || Array.isArray(group)) {
      return { ok: false, error: { code: 'group_must_be_object', index } };
    }
    const fmt = (group as { readonly asset_format?: unknown }).asset_format;
    if (typeof fmt !== 'string' || !validFormat.has(fmt)) {
      return { ok: false, error: { code: 'invalid_asset_format', index } };
    }
    const format = fmt as AssetFormat;
    if (seenFormats.has(format)) {
      return { ok: false, error: { code: 'duplicate_format', index } };
    }
    seenFormats.add(format);
    const targetsRaw = (group as { readonly targets?: unknown }).targets;
    if (!Array.isArray(targetsRaw) || targetsRaw.length === 0) {
      return { ok: false, error: { code: 'targets_must_be_non_empty_array', index } };
    }
    const compatible = new Set<string>(ASSET_FORMAT_TARGETS[format]);
    const seenTargets = new Set<string>();
    const targets: PublishTargetV1[] = [];
    for (let targetIndex = 0; targetIndex < targetsRaw.length; targetIndex += 1) {
      const target = targetsRaw[targetIndex];
      if (typeof target !== 'string' || !validTarget.has(target)) {
        return { ok: false, error: { code: 'invalid_target', index, targetIndex } };
      }
      if (!compatible.has(target)) {
        return { ok: false, error: { code: 'incompatible_target', index, targetIndex } };
      }
      if (!allowed.has(target)) {
        return { ok: false, error: { code: 'target_not_in_platforms', index, targetIndex } };
      }
      if (seenTargets.has(target)) {
        return { ok: false, error: { code: 'duplicate_target', index, targetIndex } };
      }
      seenTargets.add(target);
      targets.push(target as PublishTargetV1);
    }
    out.push({ format, targets });
  }
  return { ok: true, value: out };
}
